import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { GuidelineClauseVariation } from '~/redux/api/types/playbooks/guidelines';
import { Playbook } from '~/redux/api/types/playbooks/playbooks';

import { Guidelines } from '../guidelines/Guidelines';
import { ImportVariation } from '../import-variation/ImportVariation';
import { ScopeSelect } from '../scope-select/ScopeSelect';

type Props = {
  value?: Partial<Playbook>;
  onChange: (value: Playbook) => void;
};

export const Form = ({ value = {}, onChange }: Props) => {
  const intl = useIntl();
  const [selectedGuidelineIndex, setSelectedGuidelineIndex] = useState<
    number | null
  >(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleInserVariation = (variation: GuidelineClauseVariation) => {
    if (selectedGuidelineIndex === null) return;

    const updatedGuidelines = value.guidelines?.map((g, index) => {
      if (index === selectedGuidelineIndex) {
        return {
          ...g,
          variations: [...(g.variations ?? []), variation],
        };
      }
      return g;
    });

    onChange({
      ...value,
      guidelines: updatedGuidelines,
    } as Playbook);

    setSelectedGuidelineIndex(null);
    setTimeout(() => {
      containerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }, 100);
  };

  return (
    <div ref={containerRef}>
      <Layout spacing={4} flex="1">
        <Box flex="2">
          <Panel
            borderRadius="l"
            title={intl.formatMessage({
              id: 'playbooks.Form.playbookDetails',
              defaultMessage: 'Playbook',
              description:
                'Title for the playbook details section in the playbook setup form',
            })}
          >
            <Layout direction="column" spacing={3}>
              <Layout direction="column" spacing={4}>
                <FormField
                  input={TextInput}
                  label={intl.formatMessage({
                    id: 'playbooks.Form.playbookNameLabel',
                    defaultMessage: 'Playbook Name',
                    description: 'Label for playbook name input field',
                  })}
                  name="playbookName"
                  value={value.name}
                  inputProps={{
                    maxLength: 100,
                  }}
                  onChange={(name) => {
                    onChange({
                      ...value,
                      name: name as unknown as string,
                    } as Playbook);
                  }}
                  placeholder={intl.formatMessage({
                    id: 'playbooks.Form.playbookNamePlaceholder',
                    defaultMessage: 'e.g. Non-Disclosure Agreements',
                    description: 'Placeholder for playbook name input field',
                  })}
                />
                <FormField
                  input={TextInput}
                  label={intl.formatMessage({
                    id: 'playbooks.Form.descriptionLabel',
                    defaultMessage: 'Description',
                    description: 'Label for description input field',
                  })}
                  optional
                  name="playbookDescription"
                  value={value.description}
                  onChange={(description) => {
                    onChange({
                      ...value,
                      description: description as unknown as string,
                    } as Playbook);
                  }}
                  placeholder={intl.formatMessage({
                    id: 'playbooks.Form.descriptionPlaceholder',
                    defaultMessage:
                      'e.g. Use this playbook for all third-party Non-Disclosure Agreements',
                    description: 'Placeholder for description input field',
                  })}
                />
                <FormField
                  input={ScopeSelect}
                  label={intl.formatMessage({
                    id: 'playbooks.Form.scopeLabel',
                    defaultMessage: 'Scope',
                    description: 'Label for scope select field',
                  })}
                  description={intl.formatMessage({
                    id: 'playbooks.Form.scopeDescription',
                    defaultMessage:
                      'Select the Workflows or Contract Type this Playbook is available for.',
                    description: 'Description for scope select field',
                  })}
                  name="playbookScope"
                  value={value.restrictions}
                  onChange={(updatedRestrictions) => {
                    onChange({
                      ...value,
                      restrictions: {
                        ...(value.restrictions ?? {}),
                        ...updatedRestrictions,
                      },
                    } as Playbook);
                  }}
                />
              </Layout>

              <Layout direction="column" spacing={1} mt={2}>
                <Text variant="section">
                  <FormattedMessage
                    id="playbooks.Form.guidelinesHeader"
                    defaultMessage="Guidelines"
                    description="Header for guidelines section"
                  />
                </Text>
                <Text>
                  <FormattedMessage
                    id="playbooks.Form.guidelinesDescription"
                    defaultMessage="Enter the guidelines and sample wording that the AI can review documents on."
                    description="Description for guidelines section"
                  />
                </Text>
                <Guidelines
                  guidelines={value.guidelines ?? []}
                  onAddExistingVariation={(index) => {
                    setSelectedGuidelineIndex(index);
                    containerRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }}
                  onAddNewVariation={() => {}}
                  onChange={(updatedGuidelines) => {
                    onChange({
                      ...value,
                      guidelines: updatedGuidelines,
                    } as Playbook);
                  }}
                />
              </Layout>
            </Layout>
          </Panel>
        </Box>
        {selectedGuidelineIndex !== null && (
          <Box flex="1">
            <Panel
              borderRadius="l"
              icon={{
                icon: 'library',
                tooltip: intl.formatMessage({
                  id: 'playbooks.Form.playbookAddClauseVariationTooltip',
                  defaultMessage: 'Library',
                  description:
                    'Tooltip for library icon in add clause variation panel',
                }),
              }}
              title={intl.formatMessage({
                id: 'playbooks.Form.playbookAddClauseVariation',
                defaultMessage: 'Add Existing Clause Variation',
                description:
                  'Title for the add clause variation section in the playbook setup form',
              })}
              actions={[
                {
                  onClick: () => setSelectedGuidelineIndex(null),
                  icon: 'left-panel',
                  text: intl.formatMessage({
                    id: 'playbooks.Form.closeLeftPanel',
                    defaultMessage: 'Close',
                    description:
                      'Text for back to guidelines button in add clause variation panel',
                  }),
                },
              ]}
            >
              <ImportVariation onInsertVariation={handleInserVariation} />
            </Panel>
          </Box>
        )}
      </Layout>
    </div>
  );
};
